<template>
	<v-container>
		<p>
			The variable is scored on a scale from 0 to 100, with 100 representing the highest level of environmental performance. This map represents these scores, highlighting the Environmental Performance Index (EPI) of {{ countryCount }} countries, combining data on 47 performance indicators, focusing on climate change performance, environmental health, and ecosystem vitality.
		</p>
		<p style="font-weight: bold;">
			Source:
			<a :href="sourceUrl" target="_blank" rel="noopener noreferrer">
				{{ source }}
			</a>
		</p>
		<v-card>
			<div class="en-map-frame">
				<div class="en-world-map" @mouseleave="scheduleClearHover">
					<svg
						class="world-map-svg"
						:viewBox="world.viewBox"
						role="img"
						aria-label="Environmental Performance Index"
					>
						<path
							v-for="location in world.locations"
							:key="location.id"
							:d="location.path"
							class="world-map-region"
							:class="{
								'world-map-region--active': hasScore(location.id),
								'world-map-region--hovered': hoveredId === location.id,
							}"
							:style="{ fill: getFill(location.id) }"
							@mouseenter="setHover(location.id, $event)"
						/>
					</svg>

					<div
						v-if="hovered"
						class="map-tooltip"
						:style="tooltipStyle"
						@mouseenter="cancelClearHover"
						@mouseleave="scheduleClearHover"
					>
						<p class="map-tooltip-title mb-1">{{ hovered.name }}</p>
						<div class="map-tooltip-rule"></div>
						<p class="map-tooltip-line mb-0">
							Value : <strong>{{ formatScore(hovered.score) }} %</strong>
						</p>
						<p class="map-tooltip-line mb-0">
							Year : <strong>{{ hovered.year }}</strong>
						</p>
					</div>
				</div>

				<div class="map-legend">
					<div class="legend-bar"></div>
					<div class="legend-ticks">
						<span v-for="tick in legendTicks" :key="tick">{{ tick }}</span>
					</div>
				</div>
				<p class="map-credit mb-0">
					Source:
					<a :href="sourceUrl" target="_blank" rel="noopener noreferrer">Environmental Performance Index</a>
				</p>
			</div>
		</v-card>
	</v-container>
</template>

<script>
import world from "@svg-maps/world"
import epi from "../../data/epiScores.json"

function scoreColor(score) {
	const t = Math.max(0, Math.min(1, Number(score) / 100))
	const stops = [
		[198, 230, 176],
		[112, 196, 96],
		[46, 156, 72],
		[20, 110, 58],
		[6, 62, 36],
	]
	const scaled = t * (stops.length - 1)
	const index = Math.min(stops.length - 2, Math.floor(scaled))
	const local = scaled - index
	const from = stops[index]
	const to = stops[index + 1]
	const channel = (i) => Math.round(from[i] + (to[i] - from[i]) * local)
	return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`
}

export default {
	data() {
		return {
			world,
			scores: epi.scores || {},
			source: epi.source,
			sourceUrl: epi.sourceUrl,
			legendTicks: [10, 20, 30, 40, 50, 60, 70, 80, 90],
			hoveredId: null,
			tooltipX: 0,
			tooltipY: 0,
			hideTooltipTimer: null,
		}
	},
	computed: {
		countryCount() {
			return Object.keys(this.scores).length
		},
		hovered() {
			if (!this.hoveredId) return null
			return this.scores[this.hoveredId] || null
		},
		tooltipStyle() {
			return {
				left: `${this.tooltipX}px`,
				top: `${this.tooltipY}px`,
			}
		},
	},
	beforeDestroy() {
		this.cancelClearHover()
	},
	methods: {
		hasScore(id) {
			return Boolean(this.scores[id])
		},
		getFill(id) {
			const entry = this.scores[id]
			if (!entry) return "#e6e6e6"
			return scoreColor(entry.score)
		},
		formatScore(score) {
			return Number(score).toFixed(1)
		},
		setHover(id, event) {
			if (!this.scores[id]) return
			this.cancelClearHover()
			this.hoveredId = id
			if (event && this.$el) {
				const mapRoot = this.$el.querySelector(".en-world-map")
				const bounds = (mapRoot || this.$el).getBoundingClientRect()
				this.tooltipX = event.clientX - bounds.left + 12
				this.tooltipY = event.clientY - bounds.top + 12
			}
		},
		scheduleClearHover() {
			this.cancelClearHover()
			this.hideTooltipTimer = window.setTimeout(() => {
				this.hoveredId = null
				this.hideTooltipTimer = null
			}, 250)
		},
		cancelClearHover() {
			if (this.hideTooltipTimer) {
				window.clearTimeout(this.hideTooltipTimer)
				this.hideTooltipTimer = null
			}
		},
	},
}
</script>

<style scoped>
.en-map-frame {
	width: min(100%, 760px);
	margin: 8px auto 12px;
}

.en-world-map {
	position: relative;
	width: 100%;
}

.world-map-svg {
	display: block;
	width: 100%;
	height: auto;
}

.world-map-region {
	stroke: #ffffff;
	stroke-width: 0.4;
}

.world-map-region--active {
	cursor: pointer;
}

.world-map-region--hovered {
	stroke: #5c5c5c;
	stroke-width: 1.2;
}

.map-tooltip {
	position: absolute;
	z-index: 2;
	min-width: 140px;
	padding: 10px 12px 8px;
	border: 1px solid #d0d0d0;
	border-radius: 2px;
	background: #ffffff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	pointer-events: auto;
}

.map-tooltip-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1a1a1a;
}

.map-tooltip-rule {
	height: 3px;
	margin: 4px 0 8px;
	background: #1a1a1a;
}

.map-tooltip-line {
	font-size: 0.9rem;
	color: #333333;
	line-height: 1.45;
}

.map-legend {
	width: 100%;
	margin: 0;
	padding: 0;
}

.legend-bar {
	height: 18px;
	background: linear-gradient(
		90deg,
		rgb(198, 230, 176) 0%,
		rgb(112, 196, 96) 25%,
		rgb(46, 156, 72) 50%,
		rgb(20, 110, 58) 75%,
		rgb(6, 62, 36) 100%
	);
}

.legend-ticks {
	display: flex;
	justify-content: space-between;
	margin-top: 2px;
	padding: 0 4%;
	font-size: 0.75rem;
	color: #616161;
}

.map-credit {
	margin: 2px 0 10px;
	font-size: 0.75rem;
	color: #757575;
	text-align: left;
}
</style>
